import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Plus, Tag, ListTree, X, ChevronDown, Pencil, Trash2, Star, Building2, Handshake } from "lucide-react";

const UNITS = ["hour", "day", "item", "fixed"];
const SERVICE_TYPE_LABEL = { in_house: "In-house", outsourced: "Outsourced" };

function fmt(n, ccy = "GBP") {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(n || 0);
  } catch {
    return `${ccy} ${(Number(n) || 0).toFixed(2)}`;
  }
}

const EMPTY_PRODUCT = { code: "", name: "", description: "", category: "", unit: "hour", default_unit_price: "", currency: "GBP", is_active: true, service_type: "in_house", vendor_name: "", outsourced_cost: "" };
const EMPTY_LIST = { name: "", description: "", currency: "GBP", is_default: false };

export default function Catalog() {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [listDetail, setListDetail] = useState(null);

  const [showProductModal, setShowProductModal] = useState(false);
  const [productForm, setProductForm] = useState(EMPTY_PRODUCT);
  const [editProduct, setEditProduct] = useState(null);

  const [showListModal, setShowListModal] = useState(false);
  const [listForm, setListForm] = useState(EMPTY_LIST);

  const [priceEdits, setPriceEdits] = useState({}); // product_id -> unit_price string
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pRes, lRes] = await Promise.all([
        api.get("/products"),
        api.get("/price-lists"),
      ]);
      setProducts(pRes.data || []);
      setPriceLists(lRes.data || []);
    } catch {
      toast.error("Failed to load catalog");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openListDetail = async (pl) => {
    try {
      const { data } = await api.get(`/price-lists/${pl.price_list_id}`);
      setListDetail(data);
      const edits = {};
      for (const it of data.items || []) edits[it.product_id] = String(it.unit_price);
      setPriceEdits(edits);
    } catch {
      toast.error("Failed to load price list");
    }
  };

  const reloadListDetail = async (id) => {
    const { data } = await api.get(`/price-lists/${id}`);
    setListDetail(data);
  };

  const saveProduct = async () => {
    if (!productForm.code.trim() || !productForm.name.trim()) return toast.error("Code and name are required");
    setSaving(true);
    try {
      const body = {
        ...productForm,
        default_unit_price: parseFloat(productForm.default_unit_price) || 0,
        vendor_name: productForm.service_type === "outsourced" ? (productForm.vendor_name || null) : null,
        outsourced_cost: productForm.service_type === "outsourced" && productForm.outsourced_cost !== ""
          ? parseFloat(productForm.outsourced_cost) : null,
      };
      if (editProduct) {
        await api.put(`/products/${editProduct.product_id}`, body);
        toast.success("Product updated");
      } else {
        await api.post("/products", body);
        toast.success("Product created");
      }
      setShowProductModal(false);
      setProductForm(EMPTY_PRODUCT);
      setEditProduct(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete product "${p.name}" (${p.code})?`)) return;
    try {
      await api.delete(`/products/${p.product_id}`);
      toast.success("Product deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete product");
    }
  };

  const saveList = async () => {
    if (!listForm.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      await api.post("/price-lists", listForm);
      toast.success("Price list created");
      setShowListModal(false);
      setListForm(EMPTY_LIST);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create price list");
    } finally {
      setSaving(false);
    }
  };

  const deleteList = async (pl) => {
    if (!window.confirm(`Delete price list "${pl.name}"?`)) return;
    try {
      await api.delete(`/price-lists/${pl.price_list_id}`);
      toast.success("Price list deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete price list");
    }
  };

  const savePrice = async (product_id) => {
    const val = priceEdits[product_id];
    if (val === undefined || val === "") return;
    try {
      await api.put(`/price-lists/${listDetail.price_list_id}/items`, { product_id, unit_price: parseFloat(val) || 0 });
      await reloadListDetail(listDetail.price_list_id);
      toast.success("Price saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save price");
    }
  };

  const removePrice = async (product_id) => {
    try {
      await api.delete(`/price-lists/${listDetail.price_list_id}/items/${product_id}`);
      await reloadListDetail(listDetail.price_list_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove override");
    }
  };

  if (listDetail) {
    const overridden = new Set((listDetail.items || []).map(i => i.product_id));
    return (
      <DarkPage><div className="p-6 max-w-4xl mx-auto">
        <button onClick={() => setListDetail(null)} className="text-xs text-[#7d8590] hover:text-white mb-4 flex items-center gap-1">
          <ChevronDown className="w-3 h-3 rotate-90" /> Back
        </button>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              {listDetail.name}
              {listDetail.is_default && <Star className="w-4 h-4 text-amber-400 fill-amber-400" />}
            </h1>
            <p className="text-sm text-[#7d8590] mt-1">{listDetail.description || "No description"} · {listDetail.currency}</p>
          </div>
        </div>

        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#21262d]">
                {["Code", "Product", "Default price", "Override price", ""].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs text-[#7d8590] font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.product_id} className="border-b border-[#21262d] last:border-0">
                  <td className="px-4 py-3 text-[#00F5D4] font-mono">{p.code}</td>
                  <td className="px-4 py-3 text-white">{p.name}</td>
                  <td className="px-4 py-3 text-[#7d8590]">{fmt(p.default_unit_price, p.currency)} / {p.unit}</td>
                  <td className="px-4 py-3">
                    <input
                      value={priceEdits[p.product_id] ?? ""}
                      onChange={e => setPriceEdits(v => ({ ...v, [p.product_id]: e.target.value }))}
                      placeholder={String(p.default_unit_price)}
                      type="number" step="0.01"
                      className="w-28 bg-[#161b22] border border-[#30363d] text-white rounded-md px-2 py-1 text-sm font-mono"
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => savePrice(p.product_id)} className="text-[11px] text-[#00F5D4] hover:underline">Save</button>
                      {overridden.has(p.product_id) && (
                        <button onClick={() => removePrice(p.product_id)} className="text-[#484f58] hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {products.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-[#7d8590] text-sm">No products in the catalog yet — add one on the Products tab first.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div></DarkPage>
    );
  }

  return (
    <DarkPage><div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Catalogue</h1>
          <p className="text-sm text-[#7d8590] mt-1">Products, SKUs and price lists for consistent billing</p>
        </div>
        {tab === "products" ? (
          <Button onClick={() => { setEditProduct(null); setProductForm(EMPTY_PRODUCT); setShowProductModal(true); }}
            className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
            <Plus className="w-4 h-4 mr-1" /> New Product
          </Button>
        ) : (
          <Button onClick={() => { setListForm(EMPTY_LIST); setShowListModal(true); }}
            className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
            <Plus className="w-4 h-4 mr-1" /> New Price List
          </Button>
        )}
      </div>

      <div className="flex bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden w-fit mb-6">
        {[["products", "Products", Tag], ["lists", "Price Lists", ListTree]].map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${tab === key ? "bg-[#1c2128] text-white font-medium" : "text-[#7d8590] hover:text-white"}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {tab === "products" ? (
        products.length === 0 ? (
          <div className="text-center py-20 text-[#7d8590] text-sm">No products yet. Add a SKU for anything you bill — a service, a role-hour, a fixed deliverable.</div>
        ) : (
          <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#21262d]">
                  {["Code", "Name", "Category", "Unit", "Default price", "Service", "Status", ""].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs text-[#7d8590] font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.product_id} className="border-b border-[#21262d] group">
                    <td className="px-4 py-3 text-[#00F5D4] font-mono font-medium">{p.code}</td>
                    <td className="px-4 py-3 text-white">{p.name}</td>
                    <td className="px-4 py-3 text-[#7d8590]">{p.category || "—"}</td>
                    <td className="px-4 py-3 text-[#7d8590]">{p.unit}</td>
                    <td className="px-4 py-3 text-white font-medium">{fmt(p.default_unit_price, p.currency)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full border flex items-center gap-1 w-fit ${p.service_type === "outsourced" ? "text-violet-400 bg-violet-500/10 border-violet-500/20" : "text-[#7d8590] bg-[#1c2128] border-[#30363d]"}`}>
                        {p.service_type === "outsourced" ? <Handshake className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
                        {SERVICE_TYPE_LABEL[p.service_type] || "In-house"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${p.is_active ? "text-[#00F5D4] bg-[#00F5D4]/10 border-[#00F5D4]/20" : "text-[#7d8590] bg-[#1c2128] border-[#30363d]"}`}>
                        {p.is_active ? "active" : "inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-2 justify-end opacity-0 group-hover:opacity-100">
                        <button onClick={() => {
                          setEditProduct(p);
                          setProductForm({
                            ...p,
                            default_unit_price: String(p.default_unit_price),
                            service_type: p.service_type || "in_house",
                            vendor_name: p.vendor_name || "",
                            outsourced_cost: p.outsourced_cost != null ? String(p.outsourced_cost) : "",
                          });
                          setShowProductModal(true);
                        }} className="text-[#484f58] hover:text-white"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deleteProduct(p)} className="text-[#484f58] hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        priceLists.length === 0 ? (
          <div className="text-center py-20 text-[#7d8590] text-sm">No price lists yet. Create one to give a client group different rates for the same catalog.</div>
        ) : (
          <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#21262d]">
                  {["Name", "Description", "Currency", "Overrides", ""].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs text-[#7d8590] font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {priceLists.map(pl => (
                  <tr key={pl.price_list_id} onClick={() => openListDetail(pl)} className="border-b border-[#21262d] hover:bg-[#161b22] cursor-pointer transition-colors group">
                    <td className="px-4 py-3 text-white font-medium flex items-center gap-1.5">
                      {pl.name}
                      {pl.is_default && <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />}
                    </td>
                    <td className="px-4 py-3 text-[#7d8590]">{pl.description || "—"}</td>
                    <td className="px-4 py-3 text-[#7d8590]">{pl.currency}</td>
                    <td className="px-4 py-3 text-[#7d8590]">{pl.item_count} product{pl.item_count === 1 ? "" : "s"}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={(e) => { e.stopPropagation(); deleteList(pl); }} className="opacity-0 group-hover:opacity-100 text-[#484f58] hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Product modal */}
      {showProductModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">{editProduct ? "Edit Product" : "New Product"}</h2>
              <button onClick={() => setShowProductModal(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Code *</label>
                  <input value={productForm.code} onChange={e => setProductForm(f => ({ ...f, code: e.target.value }))}
                    placeholder="SKU-1001"
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm font-mono" />
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Category</label>
                  <input value={productForm.category} onChange={e => setProductForm(f => ({ ...f, category: e.target.value }))}
                    placeholder="e.g. Design"
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Name *</label>
                <input value={productForm.name} onChange={e => setProductForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Description</label>
                <textarea value={productForm.description} onChange={e => setProductForm(f => ({ ...f, description: e.target.value }))} rows={2}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm resize-none" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Unit</label>
                  <select value={productForm.unit} onChange={e => setProductForm(f => ({ ...f, unit: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Default price</label>
                  <input type="number" step="0.01" value={productForm.default_unit_price} onChange={e => setProductForm(f => ({ ...f, default_unit_price: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Currency</label>
                  <select value={productForm.currency} onChange={e => setProductForm(f => ({ ...f, currency: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                    {["GBP", "USD", "EUR"].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs text-[#7d8590]">
                <input type="checkbox" checked={productForm.is_active} onChange={e => setProductForm(f => ({ ...f, is_active: e.target.checked }))} />
                Active (shows up when picking line items)
              </label>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Delivered</label>
                <div className="flex bg-[#161b22] border border-[#30363d] rounded-md overflow-hidden w-fit">
                  {Object.entries(SERVICE_TYPE_LABEL).map(([key, label]) => (
                    <button key={key} type="button" onClick={() => setProductForm(f => ({ ...f, service_type: key }))}
                      className={`px-3 py-1.5 text-xs transition-colors ${productForm.service_type === key ? "bg-[#1c2128] text-white font-medium" : "text-[#7d8590] hover:text-white"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {productForm.service_type === "outsourced" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#7d8590] mb-1 block">Vendor</label>
                    <input value={productForm.vendor_name} onChange={e => setProductForm(f => ({ ...f, vendor_name: e.target.value }))}
                      placeholder="e.g. Acme Contractors"
                      className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-[#7d8590] mb-1 block">Vendor cost</label>
                    <input type="number" step="0.01" value={productForm.outsourced_cost} onChange={e => setProductForm(f => ({ ...f, outsourced_cost: e.target.value }))}
                      placeholder="Cost to you, for margin"
                      className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={saveProduct} disabled={saving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {saving ? "Saving…" : (editProduct ? "Save changes" : "Create product")}
              </Button>
              <Button onClick={() => setShowProductModal(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Price list modal */}
      {showListModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">New Price List</h2>
              <button onClick={() => setShowListModal(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Name *</label>
                <input value={listForm.name} onChange={e => setListForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Enterprise pricing"
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Description</label>
                <input value={listForm.description} onChange={e => setListForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Currency</label>
                <select value={listForm.currency} onChange={e => setListForm(f => ({ ...f, currency: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                  {["GBP", "USD", "EUR"].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-xs text-[#7d8590]">
                <input type="checkbox" checked={listForm.is_default} onChange={e => setListForm(f => ({ ...f, is_default: e.target.checked }))} />
                Default price list
              </label>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={saveList} disabled={saving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">Create list</Button>
              <Button onClick={() => setShowListModal(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
